import { Check, Crown, X } from "lucide-react";
import { API } from "@/lib/api";
import { optColor, praise, ResultTile, ScoreRace } from "@/components/game/ResultTiles";
import { Mascot } from "@/components/game/Mascot";
import { isStructured } from "@/lib/chem";
import { StructuredBoard } from "@/modes/quiz/Structured";
import { ClassScore, Confetti } from "@/components/game/ClassScore";

export const TeamScores = ({ state, fire, turn }) => {
  if (!state.team_scores) return null;
  return (
    <div className="flex flex-wrap gap-[1vw]" data-testid="board-team-scores">
      {state.teams.map((t) => (
        <div key={t.id} className={`panel px-[1.2vw] py-[1vh] border-2 flex items-center gap-4 ${turn === t.id ? "scale-105" : ""}`}
          style={{ borderColor: turn === t.id ? t.color : `${t.color}55`, boxShadow: turn === t.id ? `0 0 24px ${t.color}66` : undefined }} data-testid={`board-team-score-${t.id}`}>
          <span className="font-display text-[min(1.6rem,2.6vh)] font-bold" style={{ color: t.color }}>{t.name}</span>
          <span className="font-display text-[min(2rem,3.2vh)] font-bold">{state.team_scores[t.id] || 0}</span>
          {fire?.[t.id] > 0 && <span className="text-[2vh] text-amber-300">{fire[t.id]} öğrenci ateş serisinde 🔥</span>}
        </div>
      ))}
    </div>
  );
};

export const Head = ({ state, remaining }) => {
  const q = state.question;
  return (
    <div className="space-y-[2vh]">
      <div className="flex items-center gap-6">
        <span className="eyebrow !text-[2vh]">Soru {state.q_number || state.q_index}{state.question_count ? ` / ${state.question_count}` : ""}</span>
        <span className="px-4 py-1 rounded-full bg-[#FACC15] text-[#1A1405] font-display text-[2.6vh] font-bold">{q.points} puan</span>
        {state.phase === "question" && !(state.mode === "wheel" && state.settings.no_timer) && (
          <span className={`ml-auto font-display text-[min(4.5rem,8vh)] leading-none font-bold ${remaining <= 5000 ? `text-red-400 ${remaining > 0 ? "anim-pulse" : ""}` : "text-white"}`} data-testid="board-timer">{Math.ceil(remaining / 1000)}</span>
        )}
      </div>
      <div className="flex gap-8 items-start">
        <h1 className="font-display text-[min(3rem,4.8vh)] font-bold leading-tight flex-1" data-testid="board-question-text">{q.text}</h1>
        {q.has_image && <img src={`${API}/play/${state.code}/image?q=${q.question_id}`} alt="Soru görseli" className="max-h-[24vh] rounded-xl" />}
      </div>
    </div>
  );
};

const OpenOptions = ({ state, onPick }) => isStructured(state.question.type) ? <StructuredBoard q={state.question} /> : (
  <div className="grid grid-cols-2 gap-[2vh]">
    {state.question.options.map((o) => {
      const Tag = onPick ? "button" : "div";
      return (
        <Tag key={o.id} onClick={onPick ? () => onPick(o.id) : undefined} data-testid={`board-option-${o.id}`}
          className={`flex items-center gap-[1.2vw] rounded-[1.5rem] border-2 px-[1.5vw] py-[2vh] bg-[#161C27] text-left ${onPick ? "hover:scale-[1.02] active:scale-95 transition-transform duration-150 cursor-pointer" : ""}`}
          style={{ borderColor: `${optColor(o)}aa`, boxShadow: `0 6px 0 ${optColor(o)}55` }}>
          <span className="h-[6vh] w-[6vh] shrink-0 rounded-2xl grid place-items-center uppercase font-display text-[3.4vh] font-bold text-[#0B0F16]" style={{ backgroundColor: optColor(o) }}>{o.id}</span>
          <span className="text-[min(2.25rem,3.4vh)] font-bold flex-1">{o.text}</span>
          {state.live_counts && <span className="font-display text-[3.4vh] font-bold">{state.live_counts[o.id]}</span>}
        </Tag>
      );
    })}
  </div>
);

const Bar = ({ label, count, pct, byTeam, teams, correct }) => (
  <div className="space-y-[0.8vh]">
    <div className="flex items-center gap-3 text-[min(1.75rem,2.8vh)] font-bold">
      <span className={correct ? "text-[#4ADE80]" : ""}>{label}</span>
      {correct && <Check className="h-[3.2vh] w-[3.2vh] text-[#22C55E]" />}
      <span className="ml-auto font-display">{count} · %{pct}</span>
    </div>
    <div className={`h-[3vh] rounded-full bg-[rgba(255,255,255,0.08)] overflow-hidden flex ${correct ? "ring-2 ring-[#22C55E]" : ""}`}>
      {byTeam ? teams.map((t) => byTeam[t.id] ? (
        <div key={t.id} style={{ width: `${(pct * byTeam[t.id]) / Math.max(count, 1)}%`, backgroundColor: t.color }} />
      ) : null) : <div style={{ width: `${pct}%`, backgroundColor: "#64748B" }} />}
      {byTeam?.none ? <div style={{ width: `${(pct * byTeam.none) / Math.max(count, 1)}%`, backgroundColor: "#94A3B8" }} /> : null}
    </div>
  </div>
);

const BoardVerdict = ({ b, state }) => {
  const team = state.teams.find((t) => t.id === b.team_id) || (b.name ? { name: b.name, color: "#FACC15" } : null);
  return (
    <div className={`flex items-center gap-[1.5vw] rounded-[1.5rem] border-2 px-[2vw] py-[2vh] anim-pop ${b.ok ? "border-[#22C55E] bg-[#22C55E]/15" : "border-red-500 bg-red-500/10 anim-shake"}`} data-testid="board-verdict">
      {b.ok ? <Check className="h-[6vh] w-[6vh] text-[#22C55E]" /> : <X className="h-[6vh] w-[6vh] text-red-400" />}
      <p className="font-display text-[min(3.5rem,6vh)] font-bold">{b.option_id || b.ok ? praise(b.ok, state.q_index) : "Süre doldu!"}</p>
      {team && <p className="ml-auto font-display text-[4vh] font-bold" style={{ color: team.color }}>{team.name} {b.ok ? `+${b.gained}` : "+0"}</p>}
    </div>
  );
};

const AllAnswered = ({ secs }) => (
  <div className="flex items-center gap-[1vw] rounded-full border-2 border-[#22C55E] bg-[#22C55E]/15 px-[1.6vw] py-[0.8vh] self-start anim-pop shrink-0" data-testid="board-all-answered">
    <span className="font-display text-[min(2.2rem,3.6vh)] font-bold text-[#4ADE80]">Tüm öğrenciler soruyu yanıtladı!</span>
    <span className="text-[2.2vh] text-slate-300">{secs} sn sonra sıradaki soru</span>
  </div>
);

const Results = ({ state }) => {
  const r = state.results;
  if (r.board_reveal) {
    const right = state.question?.options?.find((o) => o.id === r.correct_option_id);
    return (
      <div className="space-y-[2vh]" data-testid="board-reveal">
        <p className="font-display text-[5vh] font-bold text-slate-200">Tahtadan yanıtlandı · puan verilmedi</p>
        {right && <p className="text-[min(2.25rem,3.6vh)] font-bold">Doğru cevap: <span className="text-[#4ADE80]">{right.id.toUpperCase()}) {right.text}</span></p>}
        <p className="text-[2.4vh] text-slate-400">Sıradaki soruya geçiliyor…</p>
      </div>
    );
  }
  if (r.skipped) return <p className="font-display text-[6vh] font-bold text-slate-300" data-testid="board-skipped">Soru atlandı</p>;
  if (r.board) {
    const right = r.options.find((o) => o.id === r.correct_option_id);
    return (
      <div className="space-y-[2vh]" data-testid="board-results">
        <BoardVerdict b={r.board} state={state} />
        {right && <p className="text-[min(2.25rem,3.6vh)] font-bold">Doğru cevap: <span className="text-[#4ADE80]">{right.id.toUpperCase()}) {right.text}</span></p>}
        {r.explanation && <p className="text-[min(1.5rem,2.4vh)] text-slate-300 line-clamp-2">{r.explanation}</p>}
      </div>
    );
  }
  return (
    <div className="space-y-[1.4vh]" data-testid="board-results">
      {r.items && (
        <div className={`grid gap-x-[2vw] gap-y-[1.2vh] ${r.items.length > 5 ? "grid-cols-2" : ""}`} data-testid="board-structured-results">
          {r.items.map((it) => <Bar key={it.id} label={`${it.label} → ${it.correct_label}`} count={it.count} pct={it.pct} teams={state.teams} />)}
        </div>
      )}
      {r.options.length > 0 && <p className={`font-display text-[min(3rem,5vh)] font-bold leading-none anim-pop ${r.correct > 0 ? "text-[#4ADE80]" : "text-amber-300"}`} data-testid="board-praise">{praise(r.correct > 0, state.q_index)} <span className="text-[min(1.8rem,2.8vh)] text-slate-200">{r.correct > 0 ? `${r.correct} kişi doğru bildi` : "Bu soruyu kimse bilemedi"}</span></p>}
      <div className="grid grid-cols-2 gap-[1.6vh]">
        {r.options.map((o) => <ResultTile key={o.id} o={o} big correct={o.id === r.correct_option_id} testId={`board-result-option-${o.id}`} />)}
      </div>
      {r.options.length > 0 && <p className="text-[2.2vh] text-slate-400">Yanıtsız: {r.unanswered} · %{r.unanswered_pct}</p>}
      {r.accepted && <p className="text-[min(2.25rem,3.6vh)] font-bold" data-testid="board-accepted">Cevap: <span className="text-[#FACC15]">{r.accepted.join(" / ")}</span></p>}
      <p className="text-[min(1.75rem,2.8vh)] text-slate-200 font-semibold" data-testid="board-correct-count">{r.correct}/{r.participants} doğru bildi</p>
      {r.explanation && <p className="text-[min(1.5rem,2.4vh)] text-slate-300 line-clamp-2">{r.explanation}</p>}
    </div>
  );
};

const CATS = ["score", "fastest", "accuracy"];
const CAT_LABEL = { score: "En yüksek puan", fastest: "En hızlı", accuracy: "En doğru" };
const catValue = (cat, r) => (cat === "score" ? `${r.score} puan` : cat === "fastest" ? (r.avg_s != null ? `${r.avg_s} sn` : "—") : `%${r.accuracy}`);
const MEDAL = {
  1: { grad: "linear-gradient(180deg,#FDE047,#CA8A04)", edge: "#A16207", h: [100, 100] },
  2: { grad: "linear-gradient(180deg,#F1F5F9,#94A3B8)", edge: "#64748B", h: [72, 72] },
  3: { grad: "linear-gradient(180deg,#FDBA74,#C2410C)", edge: "#9A3412", h: [58, 58] },
};
const RISE_DELAY = { 3: 0, 2: 0.4, 1: 0.8 };


const Step = ({ row, place, stepH }) => {
  const m = MEDAL[place];
  return (
    <div className="flex-1 max-w-[11vw] min-w-0 flex flex-col items-center justify-end h-full" data-testid={`podium-place-${place}`}>
      {place === 1 && <Crown className="h-[3.6vh] w-[3.6vh] text-[#FACC15] anim-bob drop-shadow-[0_0_10px_rgba(250,204,21,0.8)]" />}
      {row.mascot && <Mascot id={row.mascot} size="6vh" className={place === 1 ? "anim-bounce" : "anim-bob"} />}
      <span className="anim-fade-up font-display font-bold text-center leading-tight text-[min(1.8rem,2.6vh)] truncate max-w-full px-1" style={{ color: row.color, animationDelay: `${RISE_DELAY[place] + 0.5}s` }}>{row.name}</span>
      <span className="anim-fade-up text-[1.9vh] font-semibold text-slate-200 mb-[0.6vh]" style={{ animationDelay: `${RISE_DELAY[place] + 0.6}s` }}>{row.value}</span>
      <div className="anim-rise w-full rounded-t-[1.2rem] grid place-items-start justify-center pt-[0.6vh]"
        style={{ height: `${(stepH * m.h[0]) / 100}vh`, background: m.grad, boxShadow: `inset 0 -6px 0 ${m.edge}, 0 0 30px -8px ${m.edge}`, animationDelay: `${RISE_DELAY[place]}s` }}>
        <span className="font-display font-bold leading-none text-[#0B0F16]/80" style={{ fontSize: `${Math.min(5.5, stepH * 0.38)}vh` }}>{place}.</span>
      </div>
    </div>
  );
};

const PodiumCard = ({ title, rows, stepH, testId, rest }) => {
  const [first, second, third] = rows;
  return (
    <div className="panel !rounded-[1.75rem] min-h-0 flex flex-col px-[1vw] pt-[1.5vh] pb-0 overflow-hidden" data-testid={testId}>
      <p className="font-display text-[min(1.8rem,2.8vh)] font-bold text-center text-[#FACC15]">{title}</p>
      <div className="flex-1 min-h-0 flex items-end justify-center gap-[0.6vw] pt-[1vh]">
        {second ? <Step row={second} place={2} stepH={stepH} /> : <div className="flex-1 max-w-[11vw]" />}
        {first && <Step row={first} place={1} stepH={stepH} />}
        {third ? <Step row={third} place={3} stepH={stepH} /> : <div className="flex-1 max-w-[11vw]" />}
      </div>
      {rest?.length > 0 && (
        <ol className="flex flex-wrap justify-center gap-x-[1.5vw] py-[0.8vh] text-[1.9vh] border-t border-white/10" data-testid={`${testId}-rest`}>
          {rest.map((r, i) => <li key={r.name}><b>{i + 4}.</b> <span style={{ color: r.color }}>{r.name}</span> · {r.value}</li>)}
        </ol>
      )}
    </div>
  );
};

const Section = ({ label, sets, toRows, stepH, kind, withRest }) => {
  const cats = CATS.filter((c) => sets?.[c]);
  return (
    <div className="flex-1 min-h-0 flex flex-col gap-[1vh]">
      <p className="eyebrow !text-[1.8vh]">{label}</p>
      <div className="flex-1 min-h-0 grid gap-[1.2vw]" style={{ gridTemplateColumns: cats.length > 1 ? `1.3fr repeat(${cats.length - 1}, 1fr)` : "minmax(0, 46vw)", justifyContent: "center" }}>
        {cats.map((c) => {
          const rows = toRows(sets[c], c);
          return <PodiumCard key={c} title={CAT_LABEL[c]} rows={rows.slice(0, 3)} rest={withRest && c === "score" ? rows.slice(3) : null}
            stepH={c === "score" ? stepH : stepH * 0.85} testId={`board-${kind}-podium-${c}`} />;
        })}
      </div>
    </div>
  );
};

const ClassFinal = ({ f }) => (
  <div className="h-full flex flex-col items-center justify-center gap-[3vh] relative text-center" data-testid="board-class-final">
    <Confetti delay={0.2} />
    <h1 className="font-display text-[min(4rem,7vh)] font-bold anim-fade-up">Oyun bitti! <span className="text-[#FACC15]">Tebrikler sınıf!</span></h1>
    {f.record && <p className="rounded-full bg-[#22C55E] text-[#04210F] px-[2vw] py-[1vh] font-display text-[4vh] font-bold anim-pop flex items-center gap-[1vw]" data-testid="class-final-record"><Crown className="h-[4vh] w-[4vh]" />{f.prev_best === null ? "İlk sınıf rekoru!" : "Yeni sınıf rekoru!"}</p>}
    <div className="rounded-[2.5rem] border-[4px] border-[#FACC15] bg-gradient-to-br from-[#1F2937] to-[#0B0F16] px-[5vw] py-[4vh] anim-pop shadow-[0_0_80px_rgba(250,204,21,0.35)]">
      <p className="eyebrow !text-[#FACC15] text-[2.4vh]">Sınıfın toplam puanı</p>
      <p className="font-display text-[min(16rem,26vh)] font-bold leading-none tabular-nums neon-text-yellow" data-testid="class-final-score">{f.score}</p>
    </div>
    {f.prev_best !== null && <p className="text-[2.8vh] text-slate-300" data-testid="class-final-prev">Önceki rekor: <b className="text-white">{f.prev_best}</b></p>}
    {f.target > 0 && (
      <p className={`text-[3vh] font-semibold ${f.score >= f.target ? "text-[#22C55E]" : "text-slate-300"}`} data-testid="class-final-target">
        {f.score >= f.target ? `Hedef ${f.target} puana ulaştınız!` : `Hedef ${f.target} puan · ${f.target - f.score} puan kaldı`}
      </p>
    )}
  </div>
);

const ClassBanner = ({ f }) => (
  <div className="flex flex-wrap items-center gap-[1vw] text-[2.6vh] anim-fade-up" data-testid="board-class-banner">
    <span className="rounded-full border-2 border-[#FACC15]/70 px-[1.2vw] py-[0.5vh]">Sınıfın toplam puanı: <b className="text-[#FACC15]">{f.score}</b></span>
    {f.record ? <span className="rounded-full bg-[#22C55E] text-[#04210F] px-[1.2vw] py-[0.5vh] font-bold flex items-center gap-2"><Crown className="h-[2.6vh] w-[2.6vh]" />{f.prev_best === null ? "İlk sınıf rekoru!" : "Yeni sınıf rekoru!"}</span>
      : <span className="text-slate-400">Sınıf rekoru: {f.prev_best}</span>}
  </div>
);

export const Final = ({ state }) => {
  if (state.class_final && state.settings.closed_session) return <ClassFinal f={state.class_final} />;
  const p = state.podiums;
  const ip = state.individual_podiums;
  if (!p && !ip) {
    return (
      <div className="space-y-[3vh]" data-testid="board-final">
        <h1 className="font-display text-[min(4.5rem,8vh)] font-bold">Oyun bitti!</h1>
        <p className="text-[4vh] text-slate-300">Sonuçlarını telefonundan görebilirsin.</p>
      </div>
    );
  }
  const teamOf = (id) => state.teams.find((x) => x.id === id);
  const teamRows = (rows, c) => rows.map((r) => ({ name: teamOf(r.team_id).name, color: teamOf(r.team_id).color, value: catValue(c, r) }));
  const indRows = (rows, c) => rows.map((r) => ({ name: r.name, mascot: r.mascot || "orange", color: teamOf(r.team_id)?.color || "#FACC15", value: catValue(c, r) }));
  const both = p && ip;
  const stepH = both ? 11 : 26;
  return (
    <div className="h-full flex flex-col gap-[1.5vh] relative" data-testid="board-final">
      <Confetti />
      <h1 className="font-display text-[min(3.6rem,6vh)] font-bold leading-none anim-fade-up">Oyun bitti! <span className="text-[#FACC15]">Kazananlar</span></h1>
      {state.class_final && <ClassBanner f={state.class_final} />}
      {p && <Section label="Takımlar" sets={p} toRows={teamRows} stepH={stepH} kind="team" withRest />}
      {ip && <Section label="Bireysel ilk 3" sets={ip} toRows={indRows} stepH={stepH} kind="individual" />}
    </div>
  );
};

export const QuizBoard = ({ state, remaining, send }) => {
  if (state.phase === "finished") return <Final state={state} />;
  const boardMode = state.mode === "wheel";
  const canBoard = boardMode || state.mode === "quiz";
  const closed = canBoard && Boolean(state.settings.closed_session);
  const turn = boardMode && !closed ? (state.teams.find((t) => t.id === state.spin?.turn_team) || (state.spin?.turn_name ? { id: "solo", name: state.spin.turn_name, color: "#FACC15" } : null)) : null;
  const pick = canBoard && send && !state.paused ? (id) => send({ type: "cmd", action: "board_answer", option_id: id }) : null;
  return (
    <div className="h-full flex flex-col gap-[2.5vh]">
      <Head state={state} remaining={remaining} />
      {state.phase === "question" ? <OpenOptions state={state} onPick={pick} /> : (
        <div className="flex gap-[1.5vw] min-h-0">
          <div className="flex-1 min-w-0 flex flex-col gap-[1.4vh]">{state.results?.all_answered && <AllAnswered secs={state.results.auto_next} />}<Results state={state} /></div>
          {!closed && state.score_leaders?.length > 0 && <div className="w-[24vw] shrink-0"><ScoreRace rows={state.score_leaders} title={state.teams.length ? "Takım puanları" : "Öğrenci puanları"} /></div>}
        </div>
      )}
      <div className="mt-auto flex items-end justify-between gap-6">
        {closed ? <ClassScore score={state.class_total || 0} best={state.class_best} target={state.settings.class_target} compact /> : <div className="flex items-end gap-[1.5vw] min-w-0">{!(state.phase === "results" && state.score_leaders?.length) && <TeamScores state={state} turn={turn?.id} fire={state.phase === "results" ? state.results?.team_fire : null} />}{state.class_total !== undefined && <ClassScore mini score={state.class_total} best={state.class_best} />}</div>}
        {state.phase === "question" && (closed ? <p className="font-display text-[min(2.6rem,4.4vh)] font-bold shrink-0 text-[#FACC15]" data-testid="board-closed-answer">Yanıtı tahtadan seçin</p> : turn
          ? <p className="font-display text-[min(2.6rem,4.4vh)] font-bold shrink-0" data-testid="board-turn-answer"><span style={{ color: turn.color }}>{turn.name}</span> tahtada yanıtlıyor</p>
          : <p className="font-display text-[min(3rem,5vh)] font-bold neon-text-yellow shrink-0" data-testid="board-answered">{state.answered}/{state.expected} yanıtladı</p>)}
      </div>
    </div>
  );
};
